import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar } from "@/components/User/Avatar";
import { AvatarSelector } from "@/components/User/AvatarSelector";
import { Loader2, X, Plus, Globe, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ExternalLink {
  label: string;
  url: string;
}

interface EditProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  initialData: {
    username: string;
    bio: string;
    tagline: string;
    longBio: string;
    focusTags: string[];
    externalLinks: ExternalLink[];
    avatarId: number;
  };
  onSave: (data: {
    username: string;
    bio: string;
    tagline: string;
    longBio: string;
    focusTags: string[];
    externalLinks: ExternalLink[];
    avatarId: number;
  }) => Promise<void>;
}

export function EditProfileModal({
  open,
  onOpenChange,
  userId,
  initialData,
  onSave,
}: EditProfileModalProps) {
  const [formData, setFormData] = useState(initialData);
  const [newTag, setNewTag] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData(initialData);
      setNewTag("");
    }
  }, [open, initialData]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarSelect = (avatarId: number) => {
    setFormData(prev => ({ ...prev, avatarId }));
  };

  const addTag = () => {
    const trimmedTag = newTag.trim();
    if (
      trimmedTag &&
      trimmedTag.length <= 30 &&
      formData.focusTags.length < 7 &&
      !formData.focusTags.includes(trimmedTag)
    ) {
      setFormData(prev => ({
        ...prev,
        focusTags: [...prev.focusTags, trimmedTag],
      }));
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      focusTags: prev.focusTags.filter(tag => tag !== tagToRemove),
    }));
  };

  const addLink = () => {
    if (formData.externalLinks.length < 4) {
      setFormData(prev => ({
        ...prev,
        externalLinks: [...prev.externalLinks, { label: "", url: "" }],
      }));
    }
  };

  const updateLink = (index: number, field: "label" | "url", value: string) => {
    setFormData(prev => ({
      ...prev,
      externalLinks: prev.externalLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      ),
    }));
  };

  const removeLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      externalLinks: prev.externalLinks.filter((_, i) => i !== index),
    }));
  };

  const isTaglineValid = formData.tagline.length <= 160;
  const isLongBioValid = formData.longBio.length <= 2000;
  const canSave = isTaglineValid && isLongBioValid && formData.username.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col bg-card border-border p-0">
        {/* Sticky Header */}
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Edit Profile</DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Avatar Section */}
          <div className="flex flex-col items-center gap-4">
            <Avatar userId={userId} avatarId={formData.avatarId} size="lg" />
            <AvatarSelector
              selectedAvatarId={formData.avatarId}
              onSelectAvatar={handleAvatarSelect}
            />
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={e => setFormData(prev => ({ ...prev, username: e.target.value }))}
              placeholder="Enter username"
              className="bg-background border-border"
            />
          </div>

          {/* Tagline */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="tagline">Tagline</Label>
              <span className={`text-xs ${formData.tagline.length > 160 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {formData.tagline.length}/160
              </span>
            </div>
            <Input
              id="tagline"
              value={formData.tagline}
              onChange={e => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
              placeholder="A short tagline that defines you"
              className="bg-background border-border"
              maxLength={160}
            />
            <p className="text-xs text-muted-foreground">
              This appears prominently on your profile as your identity.
            </p>
          </div>

          {/* Long Bio */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="longBio">About You</Label>
              <span className={`text-xs ${formData.longBio.length > 2000 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {formData.longBio.length}/2000
              </span>
            </div>
            <Textarea
              id="longBio"
              value={formData.longBio}
              onChange={e => setFormData(prev => ({ ...prev, longBio: e.target.value }))}
              placeholder="Share your story, expertise, and what drives your prompts..."
              rows={6}
              className="bg-background border-border resize-none"
              maxLength={2000}
            />
          </div>

          {/* Focus Tags */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Focus Tags</Label>
              <span className="text-xs text-muted-foreground">
                {formData.focusTags.length}/7
              </span>
            </div>
            
            {/* Current Tags */}
            {formData.focusTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.focusTags.map(tag => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="pl-3 pr-1 py-1 gap-1 bg-primary/10 text-primary border-primary/20"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Add Tag Input */}
            {formData.focusTags.length < 7 && (
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={e => setNewTag(e.target.value)}
                  placeholder="Add a focus area (e.g., AI, Writing)"
                  className="bg-background border-border flex-1"
                  maxLength={30}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={addTag}
                  disabled={!newTag.trim() || formData.focusTags.length >= 7}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* External Links */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>External Links</Label>
              <span className="text-xs text-muted-foreground">
                {formData.externalLinks.length}/4
              </span>
            </div>

            {/* Link Entries */}
            {formData.externalLinks.map((link, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 space-y-2">
                  <Input
                    value={link.label}
                    onChange={e => updateLink(index, "label", e.target.value)}
                    placeholder="Label (e.g., Twitter, Portfolio)"
                    className="bg-background border-border"
                    maxLength={30}
                  />
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={link.url}
                      onChange={e => updateLink(index, "url", e.target.value)}
                      placeholder="https://..."
                      className="bg-background border-border pl-9"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeLink(index)}
                  className="mt-1 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}

            {/* Add Link Button */}
            {formData.externalLinks.length < 4 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLink}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Link
              </Button>
            )}
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex justify-end gap-2 p-6 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !canSave}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
